import { useEffect, useMemo, useState, useCallback } from 'react';
import _ from 'lodash';
import moment from 'moment-timezone';
import { create } from 'zustand';
import { shallow } from 'zustand/shallow';
import { styled } from '@mui/material/styles';
import { IUserJson } from '@scale/shared/types/user';
import Rating from '@mui/material/Rating';
import { Role } from '@scale/shared/corp/projectRoles';
import { CampaignName } from '@scale/shared-types';
import {
  useProjectRolesByUser,
  useUpdateRoles,
  useUpdateLastStaffingTime,
  useQMsByProjects,
  useCreateCSATResponses,
  useFlagsForStaleRoles,
  useOperationsRosterForWeeklyRolePopUp,
  useGetCSATResponsesFromLastWeekByCampaignName,
} from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';
import {
  Flex,
  Icon,
  IconButton,
  TextField,
  Table,
  Dialog,
  Text,
  Badge,
  Tooltip,
  Strong,
  DataTable,
  Separator,
  Button,
  Spinner,
} from '@scale/scaleui-radix';
import {
  faCircleCheck as faCircleCheckSolid,
  faClose,
  faCircleXmark as faCircleXmarkSolid,
  faCircleQuestion,
} from '@fortawesome/pro-solid-svg-icons';
import { faCircleCheck, faCircleXmark } from '@fortawesome/pro-regular-svg-icons';

const FILTERED_ROLES = [Role.STO, Role.EM, Role.DeliveryOpsTeamMember, Role.QM];
const nowPst = moment.tz('America/Los_Angeles');
const REMINDER_DAYS = [1, 4]; // Monday = 1, Thursday = 4
const getLastReminderDate = () => {
  const today = nowPst.day();
  // Find the most recent reminder day that has passed
  const lastReminderDay = REMINDER_DAYS.reduce((closest, day) => {
    const diff = (today - day + 7) % 7;
    if (diff < (today - closest + 7) % 7) {
      return day;
    }
    return closest;
  }, REMINDER_DAYS[0]);

  const daysToSubtract = (today - lastReminderDay + 7) % 7;
  return new Date(nowPst.clone().subtract(daysToSubtract, 'days').startOf('day').hour(8).toDate());
};

const LAST_REMINDER_DATE = getLastReminderDate();

const StyledRating = styled(Rating)({
  '& .MuiRating-iconFilled': {
    color: '#6A5DF9',
  },
  '& .MuiRating-iconHover': {
    color: '#6A5DF9',
  },
});

interface ProjectRoleStaffingReminderProps {
  user: IUserJson;
}

interface QMInfo {
  user: string;
  score: number | null;
  comment: string | undefined;
  project: string;
  team_classification: string;
}

enum TeamClassification {
  QueueManager = 'Queue Manager',
  Consultants = 'Consultants',
}

interface CSATresponse {
  campaignName: CampaignName;
  responder: string;
  question: string;
  score: number;
  comments?: string;
  metadata?: Record<string, any>;
}

const useQMsdataStore = create<{
  qms: QMInfo[];
}>(() => {
  const qms: QMInfo[] = [];
  return {
    qms,
  };
});

export const useProjectRoleCheckupModalStore = create<{
  showModal: boolean;
  setShowModal: (show: boolean) => void;
}>(set => ({
  showModal: false,
  setShowModal: show => set({ showModal: show }),
}));

const RatingField: React.FC<{ user: string }> = ({ user }) => {
  const [data] = useQMsdataStore(s => [s.qms], shallow) as [QMInfo[]];
  const score = data.find((qm: QMInfo) => qm.user === user)?.score;
  const [tooltipText, setTooltipText] = useState<string>('');
  const scoreDescription: { [key: number]: string } = useMemo(() => {
    return {
      1: 'Very Dissatisfied',
      2: 'Dissatisfied',
      3: 'Could Improve',
      4: 'Satisfied',
      5: 'Very Satisfied',
    };
  }, []);

  return (
    <Flex align="center" justify="center" gap="8">
      <Tooltip content="Not Applicable">
        <div
          onClick={() => {
            useQMsdataStore.setState({
              qms: data.map((qm: QMInfo) => (qm.user === user ? { ...qm, score: 0 } : qm)),
            });
          }}
        >
          <Text>{score === 0 ? <Strong>N/A</Strong> : 'N/A'}</Text>
        </div>
      </Tooltip>
      <Tooltip content={tooltipText}>
        <StyledRating
          name="ratingScore"
          value={score}
          onChange={(_event, newValue) => {
            if (newValue === 0) {
              return;
            }
            useQMsdataStore.setState({
              qms: data.map((qm: QMInfo) => (qm.user === user ? { ...qm, score: newValue! } : qm)),
            });
          }}
          onChangeActive={(event, newHover) => {
            setTooltipText(scoreDescription[newHover]);
          }}
        />
      </Tooltip>
    </Flex>
  );
};

const CommentField: React.FC<{ user: string }> = ({ user }) => {
  const [data] = useQMsdataStore(s => [s.qms], shallow);
  const comment = data?.find((qm: QMInfo) => qm.user === user)?.comment;
  const [localInputValue, setLocalInputValue] = useState(comment);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalInputValue(event.target.value);
  };

  const handleBlur = () => {
    useQMsdataStore.setState({
      qms: data.map((qm: QMInfo) => (qm.user === user ? { ...qm, comment: localInputValue } : qm)),
    });
  };

  return (
    <TextField.Root
      id="text-field"
      size="1"
      className="py-1"
      type="text"
      value={localInputValue || ''}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

const columnHelper = DataTable.createColumnHelper<{
  user: string;
  score: number | null;
  comment: string | undefined;
}>();

const RATINGS_COLUMNS = [
  columnHelper.accessor('user', {
    id: 'user',
    filterFn: 'includesString',
    header: 'User',
    cell: function user(props: any) {
      return (
        <Flex align="center">
          <Text>{props.row.original.user}</Text>
        </Flex>
      );
    },
  }),
  columnHelper.accessor('score', {
    id: 'score',
    header: 'Score',
    enableColumnFilter: false,
    enableSorting: false,
    cell: function ratings(props: any) {
      return <RatingField user={props.row.original.user} />;
    },
  }),
  columnHelper.accessor('comment', {
    id: 'comment',
    header: 'Comment (optional)',
    enableColumnFilter: false,
    enableSorting: false,
    cell: function comments(props: any) {
      return <CommentField user={props.row.original.user} />;
    },
  }),
];

const ModalActions: React.FC<{
  isErrorQmsByProjects: boolean;
  isSubmitting: boolean;
  isLoadingQMsByProjects: boolean;
  roles: any;
  unassignedRoles: string[];
  checkedRoles: string[];
  handleSubmit: () => void;
  timeLeft?: number;
}> = ({
  isErrorQmsByProjects,
  isSubmitting,
  isLoadingQMsByProjects,
  roles,
  unassignedRoles,
  checkedRoles,
  handleSubmit,
  timeLeft = 0,
}) => {
  const [data] = useQMsdataStore(s => [s.qms], shallow);
  const missingReviews =
    data.length > 0
      ? data.reduce(
          (acc: number, qm: QMInfo) => (qm.score !== null && acc > 0 ? acc - 1 : acc),
          data.length >= 5 ? 5 : data.length,
        )
      : 0;

  return (
    <div className="flex items-center gap-2 justify-between">
      <Text className="text-danger-500">
        {missingReviews && !isErrorQmsByProjects
          ? `${missingReviews} QM/Consultant reviews remaining`
          : ''}
      </Text>
      <Button
        className="min-w-[100px]"
        disabled={
          isSubmitting ||
          isLoadingQMsByProjects ||
          timeLeft > 0 ||
          (!!missingReviews && !isErrorQmsByProjects) ||
          // Check if all roles are checked
          !_.every(
            roles.filter((role: { _id: string }) => !unassignedRoles.includes(role._id)),
            role => checkedRoles.includes(role._id),
          )
        }
        onClick={handleSubmit}
      >
        {isSubmitting || timeLeft > 0 ? <Spinner /> : null}
        {timeLeft > 0 ? `${timeLeft}` : isSubmitting ? 'Confirming' : 'Confirm Roles'}
      </Button>
    </div>
  );
};

const ProjectRoleStaffingReminder: React.FC<ProjectRoleStaffingReminderProps> = ({
  user,
}: ProjectRoleStaffingReminderProps) => {
  const [checkedRoles, setCheckedRoles] = useState<string[]>([]);
  const [unassignedRoles, setUnassignedRoles] = useState<string[]>([]);
  const [timeLeft, setTimeLeft] = useState(0); // State to track countdown timer before being able to submit
  const { showModal, setShowModal } = useProjectRoleCheckupModalStore();
  const [localLastStaffingUpdateTime, setLocalLastStaffingUpdateTime] = useState<Date | undefined>(
    user.lastStaffingUpdateTime,
  ); // This is a local state since when the modal info is submitted the user data state is not updated automatically
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    data: operationsRosterEmails,
    isLoading: isLoadingOperationRosterEmails,
    isError: isErrorOperationRosterEmail,
  } = useOperationsRosterForWeeklyRolePopUp();

  const isOperationsUser = useMemo(
    () =>
      (operationsRosterEmails || []).some(email => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(email)) {
          const splitEmail = email.split('@');
          const workerEmail = `${splitEmail[0]}+worker@${splitEmail[1]}`;
          const expertEmail = `${splitEmail[0]}+expert@${splitEmail[1]}`;
          return user.email === workerEmail || user.email === expertEmail;
        }
        return false;
      }),
    [operationsRosterEmails, user.email],
  );

  const {
    data: flagsForStaleRoles,
    isLoading: isLoadingForStaleRoles,
    refetch: refetchFlagsForStaleRoles,
  } = useFlagsForStaleRoles(user._id);
  const { data: projectRoles, isLoading: projectRolesIsLoading } = useProjectRolesByUser(user._id);
  const { mutateAsync: updateOrRemoveProjectRoles, isLoading: isLoadingUpdateRoles } =
    useUpdateRoles();
  const { mutateAsync: updateLastStaffingTime } = useUpdateLastStaffingTime();
  const { mutateAsync: createCSATResponses } = useCreateCSATResponses();
  const { data: csatResponses } = useGetCSATResponsesFromLastWeekByCampaignName(
    CampaignName.OverallSatisfactionWithQMs,
  );

  const handleCheckboxChange = (roleId: string, checked: boolean) => {
    if (checked) {
      setCheckedRoles([...checkedRoles, roleId]);
    } else {
      setCheckedRoles(checkedRoles.filter(r => r !== roleId));
    }
  };

  const handleUnassignRole = (role: any, unassign: boolean) => {
    if (unassign) {
      setUnassignedRoles([...unassignedRoles, role._id]);
    } else {
      setUnassignedRoles(unassignedRoles.filter(r => r !== role._id));
    }
  };

  const generateCSATResponses = useCallback(() => {
    const responses: CSATresponse[] = [];
    useQMsdataStore
      .getState()
      .qms.filter(qm => qm.score !== null && qm.score > 0)
      .forEach(qm => {
        responses.push({
          campaignName: CampaignName.OverallSatisfactionWithQMs,
          responder: user._id,
          question: qm.user,
          score: qm.score!,
          comments: qm.comment,
          metadata: { project: qm.project },
        });
      });
    return responses;
  }, [user._id]);

  const resetQMsScore = useCallback(() => {
    const data = useQMsdataStore.getState().qms;
    useQMsdataStore.setState({
      qms: data.map((qm: QMInfo) => ({ ...qm, score: 0 })),
    });
  }, []);

  const activeProjectRoles = useMemo(() => {
    return projectRoles
      ?.filter(
        (role: any) =>
          new Date(role.startDate) <= new Date() &&
          (!role.stopDate || new Date(role.stopDate) > new Date()) &&
          FILTERED_ROLES.includes(role.role),
      )
      .sort((a: any, b: any) => {
        const flagA = flagsForStaleRoles?.[a.project._id]?.flag ?? false;
        const flagB = flagsForStaleRoles?.[b.project._id]?.flag ?? false;
        if (flagA === flagB) {
          return 0;
        }
        return flagA ? 1 : -1;
      });
  }, [flagsForStaleRoles, projectRoles]);

  const handleOwnersSubmit = useCallback(async () => {
    setIsSubmitting(true);
    const removedRoles = activeProjectRoles.filter((role: any) =>
      unassignedRoles.includes(role._id),
    );

    if (removedRoles.length > 0) {
      const rolesToRemove = removedRoles.map((userRole: any) => ({
        projectRoleId: userRole._id as string,
        newStartDate: userRole.startDate as string,
        newEndDate: new Date().toISOString(),
      }));
      await updateOrRemoveProjectRoles({ rolesToUpdate: rolesToRemove });
    }

    const CSATresponses = generateCSATResponses();
    if (CSATresponses.length > 0) {
      await createCSATResponses(CSATresponses);
      resetQMsScore();
    }

    setShowModal(false);
    setCheckedRoles([]);
    setUnassignedRoles([]);
    updateLastStaffingTime(user._id);
    setLocalLastStaffingUpdateTime(new Date());
    refetchFlagsForStaleRoles();
    setIsSubmitting(false);
  }, [
    activeProjectRoles,
    createCSATResponses,
    generateCSATResponses,
    refetchFlagsForStaleRoles,
    resetQMsScore,
    setShowModal,
    unassignedRoles,
    updateLastStaffingTime,
    updateOrRemoveProjectRoles,
    user._id,
  ]);

  const hasSTORole = useMemo(() => {
    return activeProjectRoles?.some((role: any) => role.role === Role.STO);
  }, [activeProjectRoles]);

  const {
    data: QMsByProjects,
    isInitialLoading: isLoadingQMsByProjects,
    isError: isErrorQmsByProjects,
  } = useQMsByProjects(
    _.uniq(
      activeProjectRoles
        ?.filter((projectRole: { role: Role }) => projectRole.role === Role.STO)
        .map((projectRole: { project: { _id: string } }) => projectRole.project._id),
    ),
  );

  const QmsToScore = useMemo(() => {
    let qms: QMInfo[] = [];
    if (QMsByProjects) {
      activeProjectRoles?.forEach((role: any) => {
        if (role.role === Role.STO && QMsByProjects[role.project._id]) {
          qms.push(
            ...QMsByProjects[role.project._id]
              ?.filter(
                qm =>
                  qm.team_classification === TeamClassification.Consultants ||
                  qm.team_classification === TeamClassification.QueueManager,
              )
              .map((qm: any) => ({
                user: qm.email,
                score: null,
                comment: '',
                project: role.project._id,
                team_classification: qm.team_classification,
              })),
          );
        }
        qms = _.sortBy(_.uniqBy(qms, 'user'), 'user');
      });
    }
    if (!_.isEmpty(qms)) {
      useQMsdataStore.setState({ qms });
    }

    return qms;
  }, [QMsByProjects, activeProjectRoles]);

  const QmsToScoreSorted = useMemo(() => {
    return _.sortBy(QmsToScore, qm => {
      const hasResponse = (csatResponses || []).some(
        (response: {
          campaignName: CampaignName;
          responder: string;
          question: string;
          score: number;
          comments?: string;
          metadata?: Record<string, any>;
        }) => response.question === qm.user,
      );
      return hasResponse ? 1 : 0; // Sort no responses (false/0) first
    });
  }, [QmsToScore, csatResponses]);

  const shouldShowStaffingModalAutomatically = useMemo(() => {
    return (
      nowPst.isAfter(moment(LAST_REMINDER_DATE).tz('America/Los_Angeles')) && // Ensure it's past 8 AM PST
      (activeProjectRoles ?? []).some((role: any) => FILTERED_ROLES.includes(role.role)) &&
      (localLastStaffingUpdateTime
        ? moment(new Date(localLastStaffingUpdateTime))
            .tz('America/Los_Angeles')
            .isBefore(moment(LAST_REMINDER_DATE).tz('America/Los_Angeles'))
        : true)
    );
  }, [activeProjectRoles, localLastStaffingUpdateTime]);

  // Set the checked roles and unassigned roles based on the flags for stale roles
  useEffect(() => {
    const staleRoles: string[] = [];
    const confirmedRoles: string[] = [];
    activeProjectRoles?.forEach((role: any) => {
      const flag = flagsForStaleRoles?.[role.project._id]?.flag || false;
      if (flag) {
        staleRoles.push(role._id);
      } else {
        confirmedRoles.push(role._id);
      }
    });
    if (staleRoles.length > 0) {
      setUnassignedRoles(previousState => [...previousState, ...staleRoles]);
    }
    setCheckedRoles(previousState => [...previousState, ...confirmedRoles]);
  }, [flagsForStaleRoles, activeProjectRoles]);

  const shouldShowReminder = useMemo(() => {
    return (
      !projectRolesIsLoading &&
      !isLoadingOperationRosterEmails &&
      !isErrorOperationRosterEmail &&
      isOperationsUser && // is in the list of operations users for the weekly role pop up
      !isLoadingQMsByProjects &&
      !isLoadingForStaleRoles &&
      shouldShowStaffingModalAutomatically
    );
  }, [
    projectRolesIsLoading,
    isLoadingQMsByProjects,
    isLoadingForStaleRoles,
    isLoadingOperationRosterEmails,
    isErrorOperationRosterEmail,
    isOperationsUser,
    shouldShowStaffingModalAutomatically,
  ]);

  const isLoadingData = useMemo(() => {
    return (
      projectRolesIsLoading ||
      isLoadingOperationRosterEmails ||
      isErrorOperationRosterEmail ||
      isLoadingQMsByProjects ||
      isLoadingForStaleRoles
    );
  }, [
    projectRolesIsLoading,
    isLoadingOperationRosterEmails,
    isErrorOperationRosterEmail,
    isLoadingQMsByProjects,
    isLoadingForStaleRoles,
  ]);

  // Show the reminder if the user has any of the filtered roles and the last staffing update time is before last Monday
  useEffect(() => {
    if (shouldShowReminder) {
      setShowModal(true);
    }
  }, [shouldShowReminder, setShowModal]);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;
    if (showModal) {
      setTimeLeft(5); // Start countdown from 5 seconds
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            if (timer !== null) {
              clearInterval(timer);
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000); // Update every second
    }

    // Cleanup the timer when the component unmounts or before re-running this effect
    return () => {
      if (timer !== null) {
        clearInterval(timer);
      }
    };
  }, [showModal]);

  const teamQms = useMemo(() => {
    return QmsToScoreSorted?.filter(
      qm => qm.team_classification === TeamClassification.QueueManager,
    );
  }, [QmsToScoreSorted]);

  const teamConsultants = useMemo(() => {
    return QmsToScoreSorted?.filter(
      qm => qm.team_classification === TeamClassification.Consultants,
    );
  }, [QmsToScoreSorted]);

  return (
    <Dialog.Root open={showModal}>
      <Dialog.Content minWidth="1100px" className="scaleui scale-theme scale-tailwind">
        <Flex justify="between">
          <Text size="6" className="pb-4">
            Project Role Checkup
          </Text>
          <Dialog.Close>
            <IconButton
              variant="ghost"
              color="neutral"
              onClick={() => !shouldShowReminder && setShowModal(false)}
            >
              <Icon icon={faClose} label="Close" />
            </IconButton>
          </Dialog.Close>
        </Flex>
        <div className="mb-6">
          <Text size="3">
            Review all of your currently assigned roles and activity statuses for each project.
            Confirm the accuracy of your roles. Have questions? Click{' '}
            <a
              href="https://docs.google.com/document/d/1HfKdYYuLjrbVL7aacaguA-BhfNlFFlSKx_WTub_ECt4/edit?tab=t.0#heading=h.s1tpi6ibn9mx"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-500"
            >
              here
            </a>{' '}
            for help.
          </Text>
        </div>
        {isLoadingData ? (
          <div className="flex justify-center items-center h-full">
            <Spinner />
          </div>
        ) : (
          <Table.Root layout="fixed">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell width="350px">Project name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Your Role</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell width="200px">
                  <Flex gap="2" align="center">
                    Your Activity Status{' '}
                    <Tooltip content="Stale roles are project assignments where either you haven't visited the project recently or contributors haven't completed tasks in a while.">
                      <Icon icon={faCircleQuestion} label="status"></Icon>
                    </Tooltip>
                  </Flex>
                </Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell width="200px">
                  Confirm assigned roles
                </Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {activeProjectRoles?.map((role: any) => (
                <Table.Row key={role._id} align="center">
                  <Table.Cell justify="start">
                    <Text size="2">{role.project.name}</Text>
                  </Table.Cell>
                  <Table.Cell justify="start">
                    <Text size="2">{role.role}</Text>
                  </Table.Cell>
                  <Table.Cell justify="start">
                    {flagsForStaleRoles?.[role.project._id]?.flag ? (
                      <Tooltip
                        content={
                          <Flex direction="column">
                            <Text className="text-white">
                              <Strong>Last attempt date:</Strong>{' '}
                              {flagsForStaleRoles[role.project._id].last_attempt}
                            </Text>
                            <Text className="text-white">
                              <Strong>Visits in last week: </Strong>{' '}
                              {flagsForStaleRoles[role.project._id].visit_count}
                            </Text>
                          </Flex>
                        }
                      >
                        <Badge size="1" color="neutral">
                          Stale
                        </Badge>
                      </Tooltip>
                    ) : (
                      <Badge size="1" color="success">
                        Active
                      </Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell justify="center">
                    <Flex justify="start" align="center" gap="2">
                      <Icon
                        id="confirm-role-checkbox"
                        data-dd-action-name="confirm-role"
                        label="Confirmed"
                        color="success"
                        size="4"
                        className="cursor-pointer"
                        icon={checkedRoles.includes(role._id) ? faCircleCheckSolid : faCircleCheck}
                        onClick={() => {
                          handleCheckboxChange(role._id, !checkedRoles.includes(role._id));
                          if (unassignedRoles.includes(role._id)) {
                            handleUnassignRole(role, false);
                          }
                        }}
                      />
                      <Icon
                        id="unassign-role-checkbox"
                        data-dd-action-name="unassign-role"
                        label="Unassigned"
                        color="neutral"
                        size="4"
                        className="cursor-pointer"
                        icon={
                          unassignedRoles.includes(role._id) ? faCircleXmarkSolid : faCircleXmark
                        }
                        onClick={() => {
                          if (checkedRoles.includes(role._id)) {
                            handleCheckboxChange(role._id, false);
                          }
                          handleUnassignRole(role, !unassignedRoles.includes(role._id));
                        }}
                      />
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}

        {!isLoadingData && hasSTORole && !isErrorQmsByProjects && teamQms.length > 0 ? (
          <>
            <Separator className="my-6" size="4" />
            <Flex direction="column" className="my-2">
              <Text size="5">QMs Review</Text>
              <div>
                <Text className="mb-2">
                  Evaluate the Queue Managers on your projects based on their performance and
                  contributions. Use the star rating to score them and add comments if needed.
                  Select <Strong>'N/A'</Strong> if you do not have enough context or have not worked
                  directly with them.
                </Text>{' '}
                <Text className="mb-4">
                  For any questions or concerns, please contact to
                  <Text className="text-primary-500"> @Stephani Brito </Text>
                  or
                  <Text className="text-primary-500"> @Juan Ocerin </Text>
                </Text>
              </div>
            </Flex>

            <DataTable.Root
              columns={RATINGS_COLUMNS}
              data={teamQms}
              enableRowSelection={false}
              loading={isLoadingQMsByProjects}
              size="1"
            >
              {({ table }) => (
                <>
                  <DataTable.Table>
                    <DataTable.Header>
                      {table.getHeaderGroups().map(headerGroup => (
                        <DataTable.HeaderGroup key={headerGroup.id}>
                          {headerGroup.headers.map(header => (
                            <DataTable.ColumnHeaderCell key={header.id} header={header}>
                              {header.column.getCanSort() ? (
                                <DataTable.ColumnHeaderSort header={header} />
                              ) : null}
                              {header.column.getCanFilter() ? (
                                <DataTable.ColumnHeaderFilter header={header} />
                              ) : null}
                            </DataTable.ColumnHeaderCell>
                          ))}
                        </DataTable.HeaderGroup>
                      ))}
                    </DataTable.Header>
                    <DataTable.Body>
                      {table.getRowModel().rows.map(row => (
                        <DataTable.Row key={row.id} row={row} align="center">
                          {row.getVisibleCells().map(cell => (
                            <DataTable.Cell key={cell.id} cell={cell} />
                          ))}
                        </DataTable.Row>
                      ))}
                    </DataTable.Body>
                  </DataTable.Table>
                  <DataTable.Pagination>
                    {table.getState().pagination.pageSize !== 5
                      ? (table.setPageSize(5) as unknown as React.ReactNode)
                      : null}
                    <DataTable.PaginationPageNavigation />
                    <DataTable.PaginationTotal />
                  </DataTable.Pagination>
                </>
              )}
            </DataTable.Root>
          </>
        ) : null}

        {!isLoadingData && hasSTORole && !isErrorQmsByProjects && teamConsultants.length > 0 ? (
          <>
            <Separator className="my-6" size="4" />
            <Flex direction="column" className="my-2">
              <Text size="5">Consultants Review</Text>
              <div>
                <Text className="mb-2">
                  Evaluate the Consultants on your projects based on their performance and
                  contributions. Use the star rating to score them and add comments if needed.
                  Select <Strong>'N/A'</Strong> if you do not have enough context or have not worked
                  directly with them.
                </Text>{' '}
                <Text className="mb-4">
                  For any questions or concerns, please contact to
                  <Text className="text-primary-500"> @Stephani Brito </Text>
                  or
                  <Text className="text-primary-500"> @Juan Ocerin </Text>
                </Text>
              </div>
            </Flex>

            <DataTable.Root
              columns={RATINGS_COLUMNS}
              data={teamConsultants}
              enableRowSelection={false}
              loading={isLoadingQMsByProjects}
            >
              {({ table }) => (
                <>
                  <DataTable.Table>
                    <DataTable.Header>
                      {table.getHeaderGroups().map(headerGroup => (
                        <DataTable.HeaderGroup key={headerGroup.id}>
                          {headerGroup.headers.map(header => (
                            <DataTable.ColumnHeaderCell key={header.id} header={header}>
                              {header.column.getCanSort() ? (
                                <DataTable.ColumnHeaderSort header={header} />
                              ) : null}
                              {header.column.getCanFilter() ? (
                                <DataTable.ColumnHeaderFilter header={header} />
                              ) : null}
                            </DataTable.ColumnHeaderCell>
                          ))}
                        </DataTable.HeaderGroup>
                      ))}
                    </DataTable.Header>
                    <DataTable.Body>
                      {table.getRowModel().rows.map(row => (
                        <DataTable.Row key={row.id} row={row} align="center">
                          {row.getVisibleCells().map(cell => (
                            <DataTable.Cell key={cell.id} cell={cell} />
                          ))}
                        </DataTable.Row>
                      ))}
                    </DataTable.Body>
                  </DataTable.Table>
                  <DataTable.Pagination>
                    {table.getState().pagination.pageSize !== 5
                      ? (table.setPageSize(5) as unknown as React.ReactNode)
                      : null}
                    <DataTable.PaginationPageNavigation />
                    <DataTable.PaginationTotal />
                  </DataTable.Pagination>
                </>
              )}
            </DataTable.Root>
          </>
        ) : null}

        <div className="mt-4">
          <ModalActions
            isErrorQmsByProjects={isErrorQmsByProjects}
            isSubmitting={isLoadingUpdateRoles || isSubmitting}
            isLoadingQMsByProjects={isLoadingQMsByProjects}
            roles={activeProjectRoles ?? []}
            unassignedRoles={unassignedRoles}
            checkedRoles={checkedRoles}
            handleSubmit={handleOwnersSubmit}
            timeLeft={timeLeft}
          />
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default ProjectRoleStaffingReminder;
